// src/components/ui/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = location.pathname === "/";
  const isTransparent = isHomePage && !scrolled;
  const textColor = isTransparent ? "text-white" : "text-accent";
  const hoverColor = "hover:text-secondary";
  const activeColor = "text-secondary";

  const externalLinks = {
    Pickup: "https://eat.allo.restaurant/restaurant/golden-arm-indisches-restaurant",
    Reservation: "https://reservations.allo.restaurant/de/golden-arm-indisches-restaurant",
    Voucher: "https://eat.allo.restaurant/restaurant/golden-arm-indisches-restaurant/gift-cards",
  };
  const customRoutes = { middaydiscount: "/middaydiscount" };
  const menuItems = ["Home", "About", "Menu", "MiddayDiscount", "Pickup", "Reservation", "Voucher", "Contact"];
  const mobileAlwaysVisible = ["Pickup", "Reservation"];

  const makeLink = (item) => {
    const isExternal = Object.keys(externalLinks).includes(item);
    if (isExternal) return { href: externalLinks[item], external: true };
    const key = item === "Home" ? "" : item.toLowerCase();
    const overridden = customRoutes[key];
    return { href: overridden || `/${key}`, external: false };
  };

  return (
    <header className={`fixed w-full z-50 h-16 md:h-20 transition-all duration-300 ${isTransparent ? "bg-transparent" : "bg-primary shadow-md"}`}>
      <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between h-full">

        {/* Hamburger for mobile */}
        <button
          className={`md:hidden text-2xl p-2 rounded transition-colors ${isOpen ? "text-yellow-500" : textColor}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <FiX /> : <FiMenu />}
        </button>

        {/* Mobile Pickup/Reservation + EN/DE inline */}
        <div className="flex md:hidden items-center gap-2 text-sm">
          {/* Pickup/Reservation */}
          {mobileAlwaysVisible.map((item) => {
            const { href, external } = makeLink(item);
            return external ? (
              <a
                key={item}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-medium px-2 py-1 text-sm"
              >
                {t(item.toLowerCase())}
              </a>
            ) : (
              <NavLink
                key={item}
                to={href}
                className="text-white font-medium px-2 py-1 text-sm"
              >
                {t(item.toLowerCase())}
              </NavLink>
            );
          })}

          {/* Language flags side by side */}
          <button
            className="w-6 h-4 rounded-sm border border-white"
            onClick={() => i18n.changeLanguage("en")}
          >
            <img src="/assets/flags/enn.png" alt="EN" className="w-full h-full object-cover" />
          </button>
          <button
            className="w-6 h-4 rounded-sm border border-white"
            onClick={() => i18n.changeLanguage("de")}
          >
            <img src="/assets/flags/germany.png" alt="DE" className="w-full h-full object-cover" />
          </button>
        </div>

        {/* Desktop nav - Right aligned */}
        <div className="hidden md:flex items-center space-x-6 ml-auto">
          {menuItems.map((item) => {
            const { href, external } = makeLink(item);
            const key = item.toLowerCase();
            return external ? (
              <a
                key={item}
                href={href}
                className={`transition-colors ${hoverColor} ${textColor}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(key)}
              </a>
            ) : (
              <NavLink
                key={item}
                to={href}
                className={({ isActive }) => `transition-colors ${hoverColor} ${isActive ? activeColor : textColor}`}
              >
                {t(key)}
              </NavLink>
            );
          })}
          <LanguageSwitcher />
        </div>
      </div>

      {/* Mobile nav - Left sliding sidebar with transparent background only on sidebar */}
      {isOpen && (
        <nav
          ref={sidebarRef}
          className="fixed top-16 md:top-20 left-0 z-40 p-6 flex flex-col bg-white/20 backdrop-blur-sm max-w-max rounded-r-md"
          style={{ transition: "transform 0.3s ease", transform: isOpen ? "translateX(0)" : "-translateX-full" }}
        >
          <ul className="flex flex-col items-start gap-4 text-white">
            {menuItems.map((item) => {
              if (mobileAlwaysVisible.includes(item)) return null;
              const { href, external } = makeLink(item);
              const key = item.toLowerCase();
              return external ? (
                <li key={item}>
                  <a
                    href={href}
                    className="transition-colors hover:text-yellow-400 text-white"
                    onClick={() => setIsOpen(false)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(key)}
                  </a>
                </li>
              ) : (
                <li key={item}>
                  <NavLink
                    to={href}
                    className={({ isActive }) => `transition-colors ${isActive ? "text-yellow-400" : "text-white"}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t(key)}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
